import { useEffect, useRef, type ReactNode } from 'react';
import { BiX } from 'react-icons/bi';
import './FilterDrawer.css';

interface FilterDrawerProps {
  open: boolean;
  /** Unique id base for this drawer instance, used to build `aria-labelledby`. */
  id: string;
  title: string;
  onClose: () => void;
  closeAriaLabel: string;
  /** Body content — for the filters drawer this is the page's existing `filtersSlot`, unmodified
   * (live-apply, D-UI7). For the columns drawer this is the visibility switch list (§4.2). */
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Generic off-canvas drawer — docs/design/14-tables-forms-restyle-spec.md §2 (`FilterDrawer`,
 * D-UI7). Reused for both the filters drawer and the column-visibility drawer (§4.2 — "same
 * `FilterDrawer` shell"). Accessible-dialog contract copied from
 * `apps/web/src/components/common/ConfirmDialog.tsx` (focus-trap/Escape/focus-return), plus a new
 * body-scroll-lock (§2.3) and an RTL-aware inline-end slide-in (§2.4).
 */
export function FilterDrawer({
  open,
  id,
  title,
  onClose,
  closeAriaLabel,
  children,
  footer,
}: FilterDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const titleId = `${id}-title`;

  return (
    <div className="fa-filter-drawer-overlay" onClick={onClose}>
      <div
        className="fa-filter-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fa-filter-drawer__header">
          <h2 id={titleId} className="fa-filter-drawer__title">
            {title}
          </h2>
          <button
            type="button"
            ref={closeRef}
            className="fa-icon-btn"
            aria-label={closeAriaLabel}
            onClick={onClose}
          >
            <BiX aria-hidden="true" />
          </button>
        </div>
        <div className="fa-filter-drawer__body">{children}</div>
        {footer && <div className="fa-filter-drawer__footer">{footer}</div>}
      </div>
    </div>
  );
}
